import { useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useEventsModule } from '../context/EventsModuleContext';

// Church location mapping with full addresses
const CHURCH_LOCATIONS = {
  'karlskrona': {
    name: 'Vår Fru av Fatima kyrka',
    address: 'Södra Kungsgatan 1, 371 31 Karlskrona, Sweden'
  },
  'karlshamn': {
    name: 'S:t Bonifatius kyrka',
    address: 'Norra Fogdelyckegatan 26, 374 38 Karlshamn, Sweden'
  },
  'olofstrom': {
    name: 'S:t Antonius kyrka',
    address: 'Bruksgatan 8, 293 38 Olofström, Sweden'
  }
};

/**
 * Get the full location string for an event
 */
const getEventLocation = (event) => {
  // If event has a specific location, use it
  if (event.location && event.location.trim()) {
    return event.location;
  }
  
  // Otherwise, use the church info
  const churchId = event.church || '';
  const churchInfo = CHURCH_LOCATIONS[churchId.toLowerCase()];
  
  if (churchInfo) {
    return `${churchInfo.name}, ${churchInfo.address}`;
  }
  
  // Fallback to church name if available
  if (event.church) {
    return event.church.charAt(0).toUpperCase() + event.church.slice(1);
  }
  
  return '';
};

/**
 * Generate Google Calendar URL
 */
const generateGoogleCalendarUrl = (event) => {
  const startDate = new Date(event.start_datetime);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Default 1 hour duration
  
  // Format dates for Google Calendar (YYYYMMDDTHHmmssZ)
  const formatDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d{3}/g, '');
  };
  
  const location = getEventLocation(event);
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || '',
    dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
    details: event.description || '',
    location: location,
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL
 */
const generateOutlookUrl = (event) => {
  const startDate = new Date(event.start_datetime);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Default 1 hour duration
  
  const location = getEventLocation(event);
  
  const params = new URLSearchParams({
    subject: event.title || '',
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString(),
    body: event.description || '',
    location: location,
    path: '/calendar/action/compose',
    rru: 'addevent',
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate and download ICS file (Apple Calendar / iCal)
 */
const downloadIcsFile = (event) => {
  const startDate = new Date(event.start_datetime);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Default 1 hour duration
  
  // Format date for ICS (YYYYMMDDTHHmmssZ)
  const formatIcsDate = (date) => {
    return date.toISOString().replace(/-|:|\.\d{3}/g, '');
  };
  
  // Escape special characters for ICS
  const escapeIcs = (str) => {
    if (!str) return '';
    return str.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
  };
  
  const uid = `${event.uid || Date.now()}@fatimakrona.com`;
  const location = getEventLocation(event);
  
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Vår Fru av Fatima//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `DTSTART:${formatIcsDate(startDate)}`,
    `DTEND:${formatIcsDate(endDate)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DESCRIPTION:${escapeIcs(event.description)}`,
    `LOCATION:${escapeIcs(location)}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
  
  // Create and download the file
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(event.title || 'event').replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Add to Calendar Button Component
 */
const AddToCalendarButton = ({ event, isRTL }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();
  const { eventsEnabled } = useEventsModule();

  // Check if event is in the past
  const eventDate = new Date(event.start_datetime);
  const now = new Date();
  const isPast = eventDate < now;

  // Hide for past events OR when the Events module is disabled
  if (isPast || !eventsEnabled) {
    return null;
  }
  
  const handleGoogleCalendar = () => {
    window.open(generateGoogleCalendarUrl(event), '_blank');
    setIsOpen(false);
  };
  
  const handleOutlook = () => {
    window.open(generateOutlookUrl(event), '_blank');
    setIsOpen(false);
  };
  
  const handleAppleCalendar = () => {
    downloadIcsFile(event);
    setIsOpen(false);
  };
  
  return (
    <>
      {/* Calendar Icon Button with Text */}
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center gap-1 px-2 py-1 rounded-full hover:bg-yellow-100 transition-colors group ${isRTL ? 'mr-1 flex-row-reverse' : 'ml-1'}`}
        title={t('addToCalendar') || 'Lägg till i kalender'}
        data-testid="add-to-calendar-btn"
      >
        <Calendar className="w-4 h-4 text-yellow-600 group-hover:text-yellow-700" />
        <span className="text-xs text-yellow-600 group-hover:text-yellow-700 font-medium whitespace-nowrap">
          {t('addToMyCalendar') || 'Lägg till i min kalender'}
        </span>
      </button>
      
      {/* Modal Popup */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setIsOpen(false)}>
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 px-6 py-4 flex items-center justify-between">
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                <Calendar className="w-6 h-6 text-white" />
                <h3 className="text-lg font-semibold text-white">
                  {t('addToCalendar') || 'Lägg till i kalender'}
                </h3>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Event Info */}
            <div className="px-6 py-3 bg-gray-50 border-b">
              <p className="font-medium text-gray-900 truncate">{event.title}</p>
              <p className="text-sm text-gray-600">
                {eventDate.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
                {' · '}
                {eventDate.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            
            {/* Calendar Options */}
            <div className="p-4 space-y-2">
              {/* Google Calendar */}
              <button
                onClick={handleGoogleCalendar}
                className={`w-full flex items-center ${isRTL ? 'flex-row-reverse' : ''} gap-4 px-4 py-3 rounded-lg border-2 border-gray-200 hover:border-yellow-400 hover:bg-yellow-50 transition-all group`}
                data-testid="google-calendar-btn"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <span className="font-medium text-gray-700 group-hover:text-gray-900">Google Calendar</span>
              </button>
              
              {/* Outlook */}
              <button
                onClick={handleOutlook}
                className={`w-full flex items-center ${isRTL ? 'flex-row-reverse' : ''} gap-4 px-4 py-3 rounded-lg border-2 border-gray-200 hover:border-yellow-400 hover:bg-yellow-50 transition-all group`}
                data-testid="outlook-calendar-btn"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.353.228-.584.228h-8.547v-6.95l1.156.923c.093.082.208.123.344.123.137 0 .25-.04.34-.123l6.653-5.306a.58.58 0 0 1 .362-.124c.115 0 .22.04.315.119.094.08.15.18.166.3.016.118.018.237.018.357l.015-.001z"/>
                    <path fill="#0078D4" d="M15.107 7.387H24v-.462c0-.23-.08-.424-.238-.576a.795.795 0 0 0-.584-.228h-8.07v1.266z"/>
                    <path fill="#0078D4" d="M15.107 6.121h7.603V4.855h-7.603z"/>
                    <path fill="#28A8EA" d="M1.107 17.68V6.32c0-.322.117-.597.35-.824.234-.228.52-.342.858-.342h9.792c.338 0 .624.114.858.342.233.227.35.502.35.824v11.36c0 .322-.117.597-.35.824-.234.228-.52.342-.858.342H2.315c-.338 0-.624-.114-.858-.342a1.104 1.104 0 0 1-.35-.824z"/>
                    <path fill="#FFFFFF" d="M7.053 8.396c-.978 0-1.77.316-2.374.948-.605.632-.907 1.468-.907 2.508 0 1.04.302 1.876.907 2.508.604.632 1.396.948 2.374.948s1.77-.316 2.374-.948c.605-.632.907-1.468.907-2.508 0-1.04-.302-1.876-.907-2.508-.604-.632-1.396-.948-2.374-.948zm0 5.208c-.52 0-.932-.18-1.236-.54-.304-.36-.456-.856-.456-1.488 0-.632.152-1.128.456-1.488.304-.36.716-.54 1.236-.54s.932.18 1.236.54c.304.36.456.856.456 1.488 0 .632-.152 1.128-.456 1.488-.304.36-.716.54-1.236.54z"/>
                  </svg>
                </div>
                <span className="font-medium text-gray-700 group-hover:text-gray-900">Outlook</span>
              </button>
              
              {/* Apple Calendar / iCal */}
              <button
                onClick={handleAppleCalendar}
                className={`w-full flex items-center ${isRTL ? 'flex-row-reverse' : ''} gap-4 px-4 py-3 rounded-lg border-2 border-gray-200 hover:border-yellow-400 hover:bg-yellow-50 transition-all group`}
                data-testid="apple-calendar-btn"
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#333" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <span className="font-medium text-gray-700 group-hover:text-gray-900">Apple Calendar / iCal</span>
              </button>
            </div>
            
            {/* Cancel Button */}
            <div className="px-4 pb-4">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full py-2 text-gray-500 hover:text-gray-700 font-medium transition-colors"
              >
                {t('cancel') || 'Avbryt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddToCalendarButton;
